import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { dateSortComparator, EditableCellRef, EditSaveScope } from '@components/DataGrid/types';
import { applyColumnQuickFilter, caseInsensitiveIncludes } from '../columns';
import RenderHeader from '@components/DataGrid/RenderHeader';
import { HeaderSubLabel, SubColumnDef, subColumnField } from '@components/DataGrid/subColumns';
import DateSlot from '@components/DataGrid/DateSlot';
import RenderCell from '@components/DataGrid/RenderCell';
import { GridColDefExtended } from '@mui/x-data-grid-pro';
import { forwardRef, RefObject, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { EditableCell, resolveEditTargetRows } from '../types';
import { apiIsoStringFormat, parseDayJs } from '@/Infrastructure/Utilities/dayjs.type';
import { useSetScheduleMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import DateTimeRangePicker, { DateTimePickerRangeValue } from '@forms/Components/DateTimeRangePicker';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch, useAppSelector } from '@reduxStore';
import { LegStatus } from '@api/Trinium/types';

import styles from '../../RenderCell.module.css';

const scheduledDeliveryField = 'Scheduled Delivery';
const headerDisplayName = 'Scheduled Delivery';

/** Sub-values of the Scheduled Delivery column sortable via hidden companion columns */
const scheduledDeliverySubColumns: SubColumnDef<DispatchLeg>[] = [
  { key: 'ref', label: 'Delivery Ref #', valueGetter: (row) => row.delvRefNo }
];

const scheduledDeliverySubLabels: HeaderSubLabel[] = scheduledDeliverySubColumns.map((sub) => ({
  label: sub.label,
  field: subColumnField(scheduledDeliveryField, sub.key)
}));

const scheduledDeliveryDefinition: GridColDefExtended<DispatchLeg> = {
  field: scheduledDeliveryField,
  minWidth: 75,
  width: 160,
  headerName: 'Scheduled Delivery From|||Scheduled Delivery To|||Delivery Reference #',
  headerDisplayName,
  subColumns: scheduledDeliverySubColumns,
  /** Mobile cards invoke renderHeader without params (RenderCardCell), so they fall back to the
   * static secondaryText instead of mounting the grid-subscribing sort labels per card */
  renderHeader: (params) => (
    <RenderHeader
      primaryText={headerDisplayName}
      subLabels={params.field ? scheduledDeliverySubLabels : undefined}
      secondaryText='Delivery Ref #'
    />
  ),
  renderCell: ({ row }) => (
    <RenderCell
      editable
      primarySlot={
        <DateSlot
          fromDate={row.scheduledDeliveryDateFrom}
          toDate={row.scheduledDeliveryDateTo}
          usePastDueStyle
        />
      }
      secondarySlot={
        row.delvRefNo && (
          <span
            className={styles['secondary-text']}
            title={`Ref No: ${row.delvRefNo}`}>
            {row.delvRefNo}
          </span>
        )
      }
    />
  ),
  disableReorder: true,
  getApplyQuickFilterFn: (value: string) => {
    return (_, row): boolean =>
      applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => {
        const { scheduledDeliveryDateFrom, scheduledDeliveryDateTo, delvRefNo } = row;
        return (
          caseInsensitiveIncludes(value, [scheduledDeliveryDateFrom, scheduledDeliveryDateTo]) ||
          caseInsensitiveIncludes(value, [delvRefNo])
        );
      });
  },
  valueGetter: (_, row: DispatchLeg) =>
    `${row.scheduledDeliveryDateFrom?.date ?? ''} ${
      row.scheduledDeliveryDateFrom?.time ?? ''
    }|||${row.scheduledDeliveryDateTo?.date ?? ''} ${row.scheduledDeliveryDateTo?.time ?? ''}|||${row.delvRefNo ?? ''}`,
  sortable: true,
  sortComparator: (v1, v2, cellParams1) => {
    if (typeof v1 === 'string' && typeof v2 === 'string') {
      const parsedV1 = v1.split('|||')[0];
      const parsedV2 = v2.split('|||')[0];
      return dateSortComparator(parsedV1, parsedV2, cellParams1);
    }
    return 0;
  },
  renderEditableCell: (row: DispatchLeg, ref: RefObject<EditableCellRef | null>, selectedRows?: DispatchLeg[]) => (
    <ScheduledDeliveryEditCell
      row={row}
      selectedRows={selectedRows}
      ref={ref}
    />
  )
};

export default scheduledDeliveryDefinition;

const canUpdateScheduledDelivery = (row: DispatchLeg) => {
  const legStatus = getEnumValueByKey(LegStatus, row.legStatus) as LegStatus;
  return legStatus !== LegStatus.DL;
};

const ScheduledDeliveryEditCell = forwardRef<EditableCellRef, EditableCell<DispatchLeg>>(({ row, selectedRows }, ref) => {
  const appDispatch = useAppDispatch();
  const { number: terminalNumber } = useCurrentTerminal();
  const [setSchedule] = useSetScheduleMutation();
  const { scheduledDeliveryDateFrom, scheduledDeliveryDateTo } = row;
  const isLoading = useAppSelector((state) => state.dispatchBoardState.loadingSet.includes(row.legId));

  const initFrom = useMemo(() => parseDayJs(scheduledDeliveryDateFrom?.unix), [scheduledDeliveryDateFrom]);
  const initTo = useMemo(() => parseDayJs(scheduledDeliveryDateTo?.unix), [scheduledDeliveryDateTo]);

  const [rangeVal, setRangeVal] = useState<DateTimePickerRangeValue>([initFrom, initTo]);

  /** The clicked leg gates the edit; a batch save extends it to the eligible selected legs */
  const canUpdate = canUpdateScheduledDelivery(row);

  const saveHandler = useCallback(
    (scope: EditSaveScope) => {
      const [fromVal, toVal] = rangeVal;
      const fromChanged =
        (fromVal?.isValid() && initFrom?.isValid() && fromVal.diff(initFrom) !== 0) ||
        (initFrom?.isValid() && !fromVal) ||
        (!initFrom && fromVal?.isValid());
      const toChanged =
        (toVal?.isValid() && initTo?.isValid() && toVal.diff(initTo) !== 0) ||
        (initTo?.isValid() && !toVal) ||
        (!initTo && toVal?.isValid());

      if (fromChanged || toChanged) {
        const targetRows = resolveEditTargetRows(scope, row, selectedRows, canUpdateScheduledDelivery);
        // Clearing both sides has to go through the remove flag - setSchedule rejects a request where every
        // schedule field/flag is unset, and passing schdDelvTo alone as null there means "leave it as-is", not "clear it".
        const isFullClear = !fromVal?.isValid() && !toVal?.isValid();

        void setSchedule({
          terminalNumber,
          actionType: {
            data: {
              legIds: targetRows.map((targetRow) => targetRow.legId).filter((legId) => legId > 0),
              ...(isFullClear
                ? { removeSchdDelvDate: true }
                : {
                    schdDelvFrom: fromVal?.isValid() ? fromVal.format(apiIsoStringFormat) : null,
                    schdDelvTo: toVal?.isValid() ? toVal.format(apiIsoStringFormat) : null
                  })
            }
          }
        })
          .unwrap()
          .catch((error: unknown) => {
            appDispatch(appendErrorAnnouncement({ message: getApiErrorMessage(error) }));
          });
      }
    },
    [appDispatch, terminalNumber, setSchedule, initFrom, initTo, rangeVal, row, selectedRows]
  );

  /** Define the callable Ref functions */
  useImperativeHandle(ref, () => {
    return {
      saveChanges: saveHandler
    };
  }, [saveHandler]);

  return (
    <div style={{ paddingBottom: '8px' }}>
      <DateTimeRangePicker
        value={rangeVal}
        onChange={(newValue) => {
          setRangeVal(newValue);
        }}
        disabled={isLoading || !canUpdate}
        customProps={{
          labelText: 'Scheduled Delivery',
          isDirty: false,
          onClear: () => {
            setRangeVal([null, null]);
          },
          variant: 'standard',
          disabledReason: isLoading
            ? 'Updating...'
            : !canUpdate
              ? 'Scheduled Delivery cannot be changed once the leg is delivered'
              : undefined
        }}
      />
    </div>
  );
});
